// Reader for the server-ranked Top/Bottom Locations payload (issue #422).
//
// The dashboard used to rank locations itself, inside `user_aggregates.locationStats`
// — a list capped at 10 top + 10 bottom by trailing-24-month sales purely to keep the
// Firestore doc under 1 MiB. Ranking inside a payload cap meant the pool was the wrong
// set for any account with more than 20 locations, and for a ranged "Bottom Locations"
// the `events > 0` filter emptied the bottom half and left the account's *top* 10
// sorted ascending.
//
// The backend now ranks across the full eligible location set and writes the final rows
// to `user_aggregates.locationRankings`. This module just reads them.
//
// @see apps/backend/functions/utilities/locationRanking.js

/** Shape version written by buildLocationRankings(). Bump both sides together. */
export const LOCATION_RANKINGS_VERSION = 1;

/** Range key used when a widget has no explicit time range saved. */
export const DEFAULT_RANGE = "Since Inception";

const END_FOR_TYPE = {
  topLocations: "top",
  bottomLocations: "bottom",
};

/**
 * The period a ranking must have been generated inside to still describe the range
 * it is labelled with.
 *
 * Ranking on the server moves a *relative* range from render time to build time.
 * "This Month" used to be resolved in the browser on every render, so it always
 * meant the current month; a persisted "This Month" ranking means whichever month
 * the aggregate was last rebuilt in. Aggregate rebuilds are event-driven, and the
 * daily cron only enqueues accounts with a reservation dated yesterday or today —
 * so an account whose last event was in June still holds a June ranking in
 * September, and would render June's locations under a "This Month" header.
 *
 * A ranking is current when it was generated inside the enclosing period: "Last
 * Quarter" is right if it was built during *this* quarter, and so on. `null` means
 * the range never goes out of period — an all-time ranking is as stale as every
 * other cached field on the doc and no staler.
 *
 * These keys are a contract with the backend: a range the server ranks but that is
 * missing here is treated as un-current forever and falls back to the legacy capped
 * pool silently. Exported so that contract can be asserted — see the parity test in
 * apps/backend/functions/utilities/locationRanking.test.js.
 */
export const VALIDITY_WINDOW = {
  "This Week": "This Week",
  "Last Week": "This Week",
  "This Month": "This Month",
  "Last Month": "This Month",
  "This Quarter": "This Quarter",
  "Last Quarter": "This Quarter",
  "This Year": "This Year",
  "Last Year": "This Year",
  "Trailing 12 Months": "This Month",
  "Since Inception": null,
};

/**
 * Is this ranking still describing the period its label claims?
 *
 * @param {object} locationRankings
 * @param {string} rangeKey
 * @param {Function} getDateRange label -> { start: moment, end: moment }
 * @returns {boolean}
 */
const isRankingCurrent = (locationRankings, rangeKey, getDateRange) => {
  const window = VALIDITY_WINDOW[rangeKey];
  if (window === null) return true;
  if (!window) return false;

  const generated = locationRankings.generated;
  if (!generated) return false;

  const { start, end } = getDateRange(window);
  return generated >= start.unix() && generated <= end.unix();
};

/**
 * Pull one widget's server-ranked rows out of the aggregates doc.
 *
 * Returns `null` — not `[]` — whenever the payload can't serve the request, so the
 * caller can fall back to the legacy client-side path. That matters during rollout:
 * a `user_aggregates` doc written before this change has no `locationRankings` at all,
 * and those accounts must keep rendering the old result until their aggregate is
 * rebuilt rather than rendering an empty table.
 *
 * An empty array is a real answer ("ranked, nothing qualified") and is returned as-is.
 *
 * @param {object|null|undefined} locationRankings dashboard.locationRankings
 * @param {"topLocations"|"bottomLocations"} dataType
 * @param {string|null|undefined} timeRange
 * @returns {Array<object>|null}
 */
export const selectRankedLocations = (locationRankings, dataType, timeRange) => {
  const end = END_FOR_TYPE[dataType];
  if (!end) return null;

  if (!locationRankings || locationRankings.version !== LOCATION_RANKINGS_VERSION) return null;

  const ranges = locationRankings.ranges;
  if (!ranges) return null;

  const entry = ranges[timeRange || DEFAULT_RANGE];
  if (!entry) return null;

  return Array.isArray(entry[end]) ? entry[end] : null;
};

/** Rows rendered per Top/Bottom Locations widget. */
export const WIDGET_ROW_LIMIT = 10;

const sales = (row) => parseFloat(row.sales) || 0;
const salesDesc = (a, b) => sales(b) - sales(a);
const salesAsc = (a, b) => sales(a) - sales(b);

/**
 * The pre-#422 client-side ranking, kept verbatim for aggregate docs that have no
 * `locationRankings` yet.
 *
 * This ranks inside `locationStats` — at most 10 top + 10 bottom locations by
 * trailing-24-month sales — so it is wrong for any account with more than 20
 * locations. It is a rollout bridge, not a supported path: once an account's
 * aggregate is rebuilt this stops being reachable. Do not extend it.
 */
const legacyLocationRows = (dashboard, end, timeRange, {getDateRange, filterMonthly}) => {
  const locationStats = dashboard.locationStats || [];

  if (!timeRange || timeRange === DEFAULT_RANGE) {
    const rows = end === "bottom"
      ? locationStats.filter((l) => (parseInt(l.completed) || 0) > 0).sort(salesAsc)
      : [...locationStats].sort(salesDesc);
    return rows.slice(0, WIDGET_ROW_LIMIT);
  }

  const filtered = filterMonthly(dashboard.locationMonthlyData || [], getDateRange(timeRange));

  const metaMap = {};
  locationStats.forEach((ls) => { metaMap[ls.location_id] = ls; });

  const totals = {};
  filtered.forEach((entry) => {
    if (!totals[entry.location_id]) {
      totals[entry.location_id] = {location_id: entry.location_id, sales: 0, donations: 0, completed: 0, events: 0};
    }
    totals[entry.location_id].sales += entry.sales || 0;
    totals[entry.location_id].donations += entry.donations || 0;
    totals[entry.location_id].completed += entry.completed || 0;
    totals[entry.location_id].events += entry.events || 0;
  });

  const rows = Object.values(totals).map((t) => ({...metaMap[t.location_id], ...t}));

  return (end === "bottom"
    ? rows.filter((t) => (t.events || 0) > 0).sort(salesAsc)
    : rows.sort(salesDesc)
  ).slice(0, WIDGET_ROW_LIMIT);
};

/**
 * Resolve the rows for a Top/Bottom Locations widget.
 *
 * Prefers the server-ranked slice for the requested range. That slice was ranked
 * across the account's full eligible location set, so Top and Bottom are computed
 * independently and neither is drawn from the capped `locationStats` pool — when
 * it is used the capped pool is not read at all.
 *
 * Falls back to the legacy path when the doc predates the fix *or* when its ranking
 * is no longer current for a relative range. The legacy path resolves the range in
 * the browser at render time, so a stale doc still renders the right period out of
 * the wrong (capped) pool, rather than the wrong period out of the right pool.
 *
 * @param {{locationRankings?: object, locationStats?: Array, locationMonthlyData?: Array}} dashboard
 * @param {"topLocations"|"bottomLocations"} dataType
 * @param {string|null|undefined} timeRange
 * @param {{getDateRange: Function, filterMonthly: Function}} helpers widgets.js date helpers
 * @returns {Array<object>}
 */
export const resolveLocationWidgetRows = (dashboard, dataType, timeRange, helpers) => {
  const end = END_FOR_TYPE[dataType];
  if (!end) return [];

  const ranked = selectRankedLocations(dashboard.locationRankings, dataType, timeRange);
  if (ranked && isRankingCurrent(dashboard.locationRankings, timeRange || DEFAULT_RANGE, helpers.getDateRange)) {
    return ranked;
  }

  return legacyLocationRows(dashboard, end, timeRange, helpers);
};
